import {
  FormGroup,
  FormItemGroup,
  RequireMarker,
  TimesInput,
} from '../../../../../../components/form-items';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlusCircle} from '@fortawesome/pro-light-svg-icons';
import React from 'react';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {IconButton} from '../../../../../../components/Button';
import {ShiftSchedule} from '../../../../api/types';
import {isNilOrEmpty} from '../../../../utils/validate';
import {timeToDateTime} from '../../../../utils/time';
import {Interval} from 'luxon';

function RestTime() {
  const {
    control,
    formState: {errors, isSubmitted},
    getValues,
    trigger,
  } = useFormContext();

  const {fields, append, remove} = useFieldArray<{
    startTime: string;
    endTime: string;
  }>({
    control,
    name: 'restTimes',
  });

  React.useEffect(() => {
    if (fields.length === 0) {
      append({startTime: '', endTime: ''});
    }
  }, [fields]);

  return (
    <>
      <h2 className={'col-start-1 col-end-4'}>休息時間</h2>
      {fields?.map((val, idx) => {
        return (
          <FormGroup
            columnTemplates={2}
            key={val.id}
            data-test-id={`rest-time-${idx}`}
          >
            <FormItemGroup>
              <label htmlFor={`restTimes.${idx}.startTime`}>
                休息開始時間
                {idx === 0 && <RequireMarker />}
              </label>
              <div className='w-[280px]'>
                <Controller
                  control={control}
                  render={({onChange, value}) => (
                    <TimesInput
                      onChange={async v => {
                        onChange(v);
                        if (isSubmitted) {
                          await trigger(`restTimes.${idx}.endTime`);
                        }
                      }}
                      error={
                        errors.restTimes &&
                        errors.restTimes[idx]?.startTime?.message
                      }
                      value={value}
                    />
                  )}
                  name={`restTimes.${idx}.startTime`}
                  id={`restTimes.${idx}.startTime`}
                  rules={{
                    required: '休息開始時間為必填',
                    validate: restStart => {
                      const values = getValues() as ShiftSchedule;
                      const restEnd = getValues(`restTimes.${idx}.endTime`);
                      const {
                        restTimes,
                        startWorkingTime: workOnTime,
                        offWorkingTime: workOffTime,
                      } = values;

                      if (isNilOrEmpty(restStart)) {
                        return '休息開始時間為必填';
                      }

                      const workStart = isNilOrEmpty(workOnTime)
                        ? undefined
                        : timeToDateTime(workOnTime);
                      const workEnd = isNilOrEmpty(workOffTime)
                        ? undefined
                        : timeToDateTime(workOffTime, workStart);
                      const restStartDate = isNilOrEmpty(restStart)
                        ? undefined
                        : timeToDateTime(restStart, workStart);
                      const restEndDate = isNilOrEmpty(restEnd)
                        ? undefined
                        : timeToDateTime(restEnd, workStart);

                      if (workStart && workEnd) {
                        const startDateTime = timeToDateTime(
                          restStart,
                          workStart,
                        );
                        if (
                          !Interval.fromDateTimes(workStart, workEnd).contains(
                            startDateTime,
                          )
                        ) {
                          return '休息開始時間區需介於上班時間區間';
                        }
                        if (restStartDate && restEndDate) {
                          if (
                            Interval.fromDateTimes(
                              workStart,
                              restStartDate,
                            ).contains(restEndDate)
                          ) {
                            return '休息結束時間不能早於休息開始時間';
                          }
                        }
                      }

                      if (isNilOrEmpty(restTimes[idx].endTime)) {
                        return true;
                      }

                      const currentCompare = timeToDateTime(restStart);

                      const overlapTimes = restTimes
                        .filter(
                          (t, tIdx) =>
                            !isNilOrEmpty(t.startTime) &&
                            !isNilOrEmpty(t.endTime) &&
                            tIdx !== idx,
                        )
                        .map(t => {
                          const start = timeToDateTime(t.startTime);
                          const end = timeToDateTime(t.endTime, start);
                          return Interval.fromDateTimes(start, end);
                        })
                        .filter(
                          i =>
                            i.contains(currentCompare) &&
                            i.end.toMillis() !== currentCompare.toMillis(),
                        );

                      return overlapTimes.length > 0 ? '休息時間不可重疊' : true;
                    },
                  }}
                />
              </div>
            </FormItemGroup>
            <FormItemGroup>
              <label htmlFor={`restTimes.${idx}.endTime`}>
                休息結束時間
                {idx === 0 && <RequireMarker />}
              </label>
              <div className="flex">
                <div className='w-[280px]'>
                  <Controller
                    control={control}
                    name={`restTimes.${idx}.endTime`}
                    id={`restTimes.${idx}.endTime`}
                    render={({onChange, value}) => (
                      <TimesInput
                        onChange={async v => {
                          onChange(v);
                          if (isSubmitted) {
                            await trigger(`restTimes.${idx}.startTime`);
                          }
                        }}
                        error={
                          errors.restTimes &&
                          errors.restTimes[idx]?.endTime?.message
                        }
                        value={value}
                      />
                    )}
                    rules={{
                      required: '休息結束時間為必填',
                      validate: restEnd => {
                        const values = getValues() as ShiftSchedule;
                        const restStart = getValues(`restTimes.${idx}.startTime`);
                        const {
                          startWorkingTime: workOnTime,
                          offWorkingTime: workOffTime,
                          restTimes,
                        } = values;

                        if (isNilOrEmpty(restEnd)) {
                          return '休息結束時間為必填';
                        }

                        const workStart = isNilOrEmpty(workOnTime)
                          ? undefined
                          : timeToDateTime(workOnTime);
                        const workEnd = isNilOrEmpty(workOffTime)
                          ? undefined
                          : timeToDateTime(workOffTime, workStart);
                        const restStartDate = isNilOrEmpty(restStart)
                          ? undefined
                          : timeToDateTime(restStart, workStart);
                        const restEndDate = isNilOrEmpty(restEnd)
                          ? undefined
                          : timeToDateTime(restEnd, workStart);

                        if (workStart && workEnd) {
                          const endDateTime = timeToDateTime(restEnd, workStart);
                          if (
                            !Interval.fromDateTimes(workStart, workEnd).contains(
                              endDateTime,
                            )
                          ) {
                            return '休息結束時間區需介於上班時間區間';
                          }
                          if (restStartDate && restEndDate) {
                            if (
                              Interval.fromDateTimes(
                                workStart,
                                restStartDate,
                              ).contains(restEndDate)
                            ) {
                              return '休息結束時間不能早於休息開始時間';
                            }
                          }
                        }

                        if (isNilOrEmpty(restTimes[idx].startTime)) {
                          return true;
                        }

                        const currentCompare = timeToDateTime(restEnd);

                        const overlapTimes = restTimes
                          .filter(
                            (t, tIdx) =>
                              !isNilOrEmpty(t.startTime) &&
                              !isNilOrEmpty(t.endTime) &&
                              tIdx !== idx,
                          )
                          .map(t => {
                            const start = timeToDateTime(t.startTime);
                            const end = timeToDateTime(t.endTime, start);
                            return Interval.fromDateTimes(start, end);
                          })
                          .filter(
                            i =>
                              i.contains(currentCompare) &&
                              i.start.toMillis() !== currentCompare.toMillis(),
                          );

                        return overlapTimes.length > 0 ? '休息時間不可重疊' : true;
                      },
                    }}
                  />
                </div>
                <div className={'w-5 flex items-center'}>
                  {fields.length > 1 && (
                    <IconButton
                      data-test-id={`remove-rest-btn-${idx}`}
                      onClick={e => {
                        e.preventDefault();
                        remove(idx);
                      }}
                    >
                      <FontAwesomeIcon icon={['fal', 'trash-alt']} />
                    </IconButton>
                  )}
                </div>
              </div>
            </FormItemGroup>
          </FormGroup>
        );
      })}
      {fields.length < 3 && (
        <div className="mb-[20px]">
          <IconButton
            onClick={e => {
              e.preventDefault();
              append({
                startTime: '',
                endTime: '',
              });
            }}
            data-test-id={'add-rest-time'}
          >
            <FontAwesomeIcon icon={faPlusCircle} style={{fontSize: 20}} />
            新增休息時間
          </IconButton>
        </div>
      )}
    </>
  );
}

export default RestTime;
